import {
  type QueryClient,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { day } from '@vrplatform/utils';
import { useApiQuery } from '../api';
import { retry } from '../api/api.retry';
import { useDashboard } from '../hooks';
import { useTeam } from '../hooks/team';
import type { Account } from './useAccountsQuery';
import { useEffect } from 'react';

type Props = { skip?: boolean; regex?: string };

type SavedAssignments = {
  savedAt: string;
  assignments: Account['assignments'];
};

type MissingAssignmentsSnapshot = {
  computedAt: string;
  assignments: Account['assignments'];
};

// A save waits for a snapshot computed after it, or the cap. That snapshot is
// the server's answer, even when it still lists a saved type.
const isSavePending = (
  saved: SavedAssignments | undefined,
  snapshot: MissingAssignmentsSnapshot | undefined
) =>
  !!saved &&
  day().diff(saved.savedAt, 'second') < 120 &&
  // A save can finish before the first snapshot loads.
  (!snapshot || !day(snapshot.computedAt).isAfter(saved.savedAt));

const getSavedKey = (teamId: string | undefined) => [
  'accounts-assignments-missing-saved',
  teamId,
];

/**
 * `assignments` replaces the hidden types with the latest save. Without it
 * (an accounts invalidation), the hidden types are kept and only the wait is
 * extended, because the modals invalidate right after `setAssigned`.
 */
const markSaved = (
  queryClient: QueryClient,
  teamId: string | undefined,
  assignments?: Account['assignments']
) =>
  queryClient.setQueryData<SavedAssignments>(getSavedKey(teamId), (prev) => ({
    savedAt: day().format(),
    assignments:
      assignments ??
      (prev && day().diff(prev.savedAt, 'second') < 120
        ? prev.assignments
        : []),
  }));

export function useAccountsAssignmentsMissingQuery(args: Props = {}) {
  const [{ id: teamId }] = useTeam();
  const [dashboard] = useDashboard();
  const queryClient = useQueryClient();

  // VRT-688: account writes reach the team issue after its next evaluation,
  // usually within a minute. Saved assignments stay hidden until then.
  const savedKey = getSavedKey(teamId);
  const { data: saved } = useQuery<SavedAssignments>({
    queryKey: savedKey,
    queryFn: skipToken,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Every account write invalidates the accounts query group. Treat that as a
  // save without known assignments so the hook polls for the next evaluation.
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        const [group, uniqueQueryKey] = event.query.queryKey;
        if (
          event.type === 'updated' &&
          event.action.type === 'invalidate' &&
          group === 'accounts' &&
          uniqueQueryKey === 'accounts-assignments-missing' &&
          event.query.queryKey.includes(teamId)
        ) {
          markSaved(queryClient, teamId);
        }
      }),
    [queryClient, teamId]
  );

  // VRT-688: read the evaluated team issue instead of loading every account.
  const queryData = useApiQuery(
    async (api, signal, variables) => {
      const res = await api.GET('/teams/issues', {
        params: {
          query: {
            teamIds: variables.teamId,
            codes: ['missingAccountAssignments'],
            // Keeps computedAt when the team has no missing assignments.
            includeEmptyTeams: true,
          },
        },
        signal,
      });

      if (res.error) throw res.error;

      const [team] = res.data.data;
      if (!team) throw new Error('Team issues not found');

      return {
        computedAt: team.computedAt,
        assignments: team.issues.flatMap((issue): Account['assignments'] =>
          issue.code === 'missingAccountAssignments' &&
          issue.context &&
          'assignments' in issue.context
            ? issue.context.assignments
            : []
        ),
      };
    },
    {
      queryGroup: 'accounts',
      uniqueQueryKey: 'accounts-assignments-missing',
      variables: { teamId },
      skip: dashboard === 'owner' || args.skip || !teamId,
      // A new team's issue snapshot is still materializing (503, retry after
      // 15s). Keep loading, so the expense and deposit guards stay up.
      retry: (failureCount, error) =>
        error.code === 'SERVICE_UNAVAILABLE'
          ? failureCount < 12
          : retry(failureCount, error),
      poll: {
        isCompleted: (data) => !isSavePending(saved, data),
        onCompleted: saved
          ? () => queryClient.removeQueries({ queryKey: savedKey, exact: true })
          : undefined,
        interval: 5000,
      },
    }
  );

  // Not memoized: the pending check depends on the current time.
  const reg = args.regex ? new RegExp(args.regex) : null;
  const hidden = isSavePending(saved, queryData.data)
    ? saved?.assignments
    : undefined;
  const data = (queryData.data?.assignments ?? []).filter(
    (x) => (!reg || reg.test(x)) && !hidden?.includes(x)
  );

  /**
   * Call after an account write, before invalidating. Hides the saved
   * assignments until the issue reflects the write.
   */
  const setAssigned = (assignments: Account['assignments']) =>
    markSaved(queryClient, teamId, assignments);

  return {
    ...queryData,
    data,
    setAssigned,
  };
}
